/**
 * Talking to the API.
 *
 * Small on purpose. The one decision worth commenting is the error shape: a refusal from this API
 * carries `{ error, hint }`, and both are surfaced to the user. Screens that swallow the hint and
 * render "Something went wrong" throw away the half of the message that tells somebody what to do.
 */

import { useCallback, useEffect, useState } from "react";

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly hint?: string, readonly code?: string) {
    super(message);
  }
}

/**
 * The development identity headers.
 *
 * The API accepts these only outside production, and a PRODUCTION BUILD OF THIS APP NEVER SENDS
 * THEM. The two refusals are independent on purpose: the API's is the one that matters, and this
 * one exists so that a production page does not announce a tenant and an identity on every request
 * to a server that ignores them — a header nobody reads is still a header everybody can see in a
 * proxy log, and "acme / operator" on the wire from security.rathvan.com reads as a configuration
 * mistake to the first person who looks. `import.meta.env.PROD` is a build-time constant Vite
 * substitutes, so in `vite build` output this function is `return {}` and the localStorage reads
 * are gone. Outside production it reads localStorage so a reviewer can switch identity without a
 * rebuild. `api.test.ts` holds both halves, the second against a real production build.
 */
export function devHeaders(): Record<string, string> {
  if (import.meta.env.PROD) return {};
  const tenant = localStorage.getItem("hub_dev_tenant") ?? "acme";
  const identity = localStorage.getItem("hub_dev_identity") ?? "operator";
  return { "x-hub-dev-tenant": tenant, "x-hub-dev-identity": identity };
}

/**
 * Raised on `window` whenever ANY call comes back 401. The shell listens and swaps the page for
 * the sign-in screen, so a session that expires mid-visit ends in one place (a sign-in link) rather
 * than in eleven screens each rendering "Not authenticated" in their own words.
 */
export const UNAUTHENTICATED_EVENT = "hub:unauthenticated";

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...devHeaders(),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: unknown;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { error: text }; }

  if (!res.ok) {
    const b = body as { error?: string; hint?: string; code?: string };
    const err = new ApiError(res.status, b.error ?? `Request failed (${res.status}).`, b.hint, b.code);
    if (res.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT, { detail: err }));
    }
    throw err;
  }
  return body as T;
}

export interface Loaded<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Load once, and expose the three states separately.
 *
 * `loading`, `error` and `data` are distinct rather than collapsed into `data ?? []`, because an
 * empty list and a failed request look identical on screen when they are collapsed — and on a
 * screen whose job is to report that nothing is wrong, "no rows" and "we could not ask" are the two
 * answers that must never be confused.
 */
export function useApi<T>(path: string | null, deps: unknown[] = []): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path === null) { setLoading(false); return; }
    let live = true;
    setLoading(true);
    setError(null);
    api<T>(path)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof ApiError ? e : new ApiError(0, String(e)));
        setLoading(false);
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { data, error, loading, reload };
}

export function relative(iso: string): string {
  const then = Date.parse(iso);
  const mins = Math.round((Date.now() - then) / 60_000);
  if (Number.isNaN(mins)) return iso;
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Time remaining, for a TTL. Negative reads as "expired", never as a negative number. */
export function remaining(iso: string | null): string {
  if (!iso) return "no expiry";
  const secs = Math.round((Date.parse(iso) - Date.now()) / 1000);
  if (secs <= 0) return "expired";
  if (secs < 60) return `${secs}s left`;
  if (secs < 3600) return `${Math.round(secs / 60)}m left`;
  const days = secs / 86_400;
  if (days >= 1) return `${Math.round(days)}d left`;
  return `${Math.round(secs / 3600)}h left`;
}
